import { slugify } from "@/lib/utils/text";
import type { TourSummary } from "./types";

// Tur kodu ile arama (ana sayfa "Tur kodu" alanı, tur listesi `kod` filtresi). Acentelerin elindeki iki tür kod:
//  - Tur (ana tur) kodu: Search'teki tourCode, tur sayfasında "Acentenize iletmek için tur kodu" — "ATH-TK3N",
//    "FNP-XQ4N-FRA-STR", "EÇ-EK7N", "ATH-TK3N-15:40"
//  - Kalkış kodu: AltList/Load'daki tourCode — ana tur kodu + "-YYAAGG-SS:DD" (+ bazen " YYYY-AA-GG-00:00"):
//    "ATH-TK3N-261112-11:50", "AMS-TK3N-270517-14:55 2027-05-17-00:00"
// Karşılaştırma anahtarı büyük/küçük harf, boşluk, tire, iki nokta ve Türkçe harf farkını yok sayar:
// "ath tk3n" = "athtk3n" = "ATH-TK3N"; "eç-ek7n" = "EC EK7N"; "sicilya" = "SİCİLYA" (I/İ/ı/i hepsi "I").
// Saf mantık burada (birim testli); kalkış listesini getiren katman (AltList) services/tour-code.ts.

export const CODE_MAX_LENGTH = 40;

/** Kod anahtarı: Türkçe harfler ASCII'ye, yalnızca harf ve rakam, büyük harf. "Ath-tk3n " → "ATHTK3N" */
export function codeKey(input: string): string {
  return slugify(input).replace(/-/g, "").toUpperCase();
}

/** Kısmi eşleşme (tur listesi filtresi): anahtar tur kodunun içinde geçiyor ya da kod bu turun bir kalkış kodu gibi başlıyor */
export function matchesCode(tourCode: string, key: string): boolean {
  if (!key) return true;
  const own = codeKey(tourCode);
  return own.includes(key) || (own.length >= 3 && key.startsWith(own));
}

/** Bir kalkış kodunun kabul edilen yazımları: tam, sondaki tarih eki olmadan, saat olmadan (tur kodu + YYAAGG) */
export function departureCodeKeys(code: string): string[] {
  const core = code.trim().split(/\s+/)[0] ?? "";
  const noTime = core.replace(/-\d{1,2}:\d{2}$/, "");
  return [...new Set([codeKey(code), codeKey(core), codeKey(noTime)].filter(Boolean))];
}

/** Kalkış listesindeki (AltList) bir kayıt; yalnızca eşleştirme için gereken alanlar */
export type DepartureCodeRecord = { id: number; tourCode: string; startDate: string };

export type CodeResolution =
  /** Tek bir turun kodu: tur sayfasına (`departureId` varsa o kalkış seçili) */
  | { kind: "tour"; tour: TourSummary; departureId: number | null }
  /** Tam eşleşme yok: liste kısmi eşleşmeyle filtrelenir */
  | { kind: "list" };

/** Anahtarı tam olarak tur kodu olan turlar */
function exactTours(tours: TourSummary[], key: string): TourSummary[] {
  return tours.filter((t) => codeKey(t.code) === key);
}

/** Kodu bu turun kalkış kodu olabilecek turlar (tur kodu anahtarın başında), en uzun kod önce: ATH-TK3N-15:40 > ATH-TK3N */
export function departureCandidates(tours: TourSummary[], key: string): TourSummary[] {
  return tours
    .filter((t) => {
      const own = codeKey(t.code);
      return own.length >= 3 && key.length > own.length && key.startsWith(own);
    })
    .sort((a, b) => codeKey(b.code).length - codeKey(a.code).length);
}

/** Kalkış listesinden kodu birebir tutan kalkış (aynı gün birden fazla saat varsa ilki); yoksa null */
export function matchDepartureRecord(records: DepartureCodeRecord[], key: string): DepartureCodeRecord | null {
  const sorted = [...records].sort((a, b) => a.startDate.localeCompare(b.startDate) || a.id - b.id);
  return sorted.find((r) => departureCodeKeys(r.tourCode).includes(key)) ?? null;
}

/**
 * Kalkış listesi alınamadığında yedek: tur kodundan sonraki ilk altı rakam kalkış tarihi (YYAAGG) sayılır ve turun
 * o günkü kalkışı seçilir. "ATHTK3N2611121150" → 2026-11-12.
 */
export function departureByDate(tour: TourSummary, key: string): number | null {
  const rest = key.slice(codeKey(tour.code).length);
  const m = /^(\d{2})(\d{2})(\d{2})/.exec(rest);
  if (!m) return null;
  const date = `20${m[1]}-${m[2]}-${m[3]}`;
  return tour.departures.find((d) => d.date === date)?.id ?? null;
}

/**
 * Yılsız kod anahtarı. Rezervasyon sistemi 2026-10'da kodlara yıl ekledi ("ATH-TK3N" → "ATH-TK3N-2026", "ATH-TK3N-2027",
 * "AMS-PC3N-2026-3N4D"); acentelerin elindeki eski kod yıldan önceki kısımdır. Yıl yoksa null.
 */
export function baseCodeKey(tourCode: string): string | null {
  const base = tourCode.trim().replace(/-20\d\d(?:-.*)?$/, "");
  return base !== tourCode.trim() && base ? codeKey(base) : null;
}

/** Kalkışı en yakın tur (ay verildiyse o ayda kalkışı olanlar önce) ve seçilecek kalkış */
function nearestTour(tours: TourSummary[], month?: string): { tour: TourSummary; departureId: number | null } {
  const inMonth = month ? tours.filter((t) => t.departures.some((d) => d.date.startsWith(month))) : [];
  const pool = inMonth.length > 0 ? inMonth : tours;
  const tour = [...pool].sort((a, b) => (a.departures[0]?.date ?? "").localeCompare(b.departures[0]?.date ?? ""))[0];
  return { tour, departureId: (month ? tour.departures.find((d) => d.date.startsWith(month))?.id : undefined) ?? null };
}

/**
 * Eski (yılsız) kod: tur kodu → yılı eklenmiş sürümlerden kalkışı en yakın olan; kalkış kodu ("ATH-TK3N-261112-11:50")
 * → koddaki tarihte kalkışı olan sürüm ve o kalkış. Bulunamazsa null.
 */
function resolveRenamed(tours: TourSummary[], key: string, month?: string): CodeResolution | null {
  const based = tours
    .map((t) => ({ t, base: baseCodeKey(t.code) }))
    .filter((x): x is { t: TourSummary; base: string } => x.base !== null && x.base.length >= 3);
  const same = based.filter((x) => x.base === key).map((x) => x.t);
  if (same.length > 0) return { kind: "tour", ...nearestTour(same, month) };
  // En uzun yılsız kod önce: "ATH-TK3N-15:40-…" kalkış kodu ATH-TK3N'e değil ATH-TK3N-15:40 sürümlerine
  for (const { t, base } of based.filter((x) => key.length > x.base.length && key.startsWith(x.base)).sort((a, b) => b.base.length - a.base.length)) {
    const m = /^(\d{2})(\d{2})(\d{2})/.exec(key.slice(base.length));
    const dep = m ? t.departures.find((d) => d.date === `20${m[1]}-${m[2]}-${m[3]}`) : undefined;
    if (dep) return { kind: "tour", tour: t, departureId: dep.id };
  }
  return null;
}

/**
 * Kodu çözer. Sıra: (1) tek bir turun tur kodu → o tur (ay seçiliyse o aydaki ilk kalkış); (2) bir turun kalkış kodu →
 * tur + kalkış (`loadDepartures` turun kalkış listesini verir; alınamazsa null → tarih yedeği); (3) yılsız eski kod →
 * yılı eklenmiş sürüm (resolveRenamed); (4) liste.
 * Aynı anahtara iki tur denk gelirse (yazım farkı yalnızca tire/boşluk) yönlendirilmez, ikisi listelenir.
 */
export async function resolveCode(
  tours: TourSummary[],
  raw: string,
  loadDepartures: (tour: TourSummary) => Promise<DepartureCodeRecord[] | null>,
  month?: string,
): Promise<CodeResolution> {
  const key = codeKey(raw);
  if (!key) return { kind: "list" };
  const exact = exactTours(tours, key);
  if (exact.length === 1) {
    const tour = exact[0];
    const inMonth = month ? tour.departures.find((d) => d.date.startsWith(month)) : undefined;
    return { kind: "tour", tour, departureId: inMonth?.id ?? null };
  }
  if (exact.length > 1) return { kind: "list" };

  // Kalkış kodu: ilk altı rakam tarih olmalı (tur kodu + YYAAGG…); aday tur sayısı en fazla birkaç
  for (const tour of departureCandidates(tours, key).slice(0, 4)) {
    if (!/^\d{6}/.test(key.slice(codeKey(tour.code).length))) continue;
    const records = await loadDepartures(tour);
    const own = new Set(tour.departures.map((d) => d.id));
    // Önce kalkış listesinde birebir kod; yoksa (liste alınamadı ya da saat farklı yazıldı) koddaki tarih
    const record = records ? matchDepartureRecord(records, key) : null;
    // Geçmiş/satışta olmayan kalkışın kodu: tur sayfası açılır, kalkış seçimi sayfanın varsayılanı
    if (record) return { kind: "tour", tour, departureId: own.has(record.id) ? record.id : null };
    const byDate = departureByDate(tour, key);
    if (byDate !== null) return { kind: "tour", tour, departureId: byDate };
  }
  return resolveRenamed(tours, key, month) ?? { kind: "list" };
}

/** Eşleşme yokken önerilecek kodlar: ortak başlangıcı en uzun (en az 2 karakter) turlar, en fazla `limit` */
export function suggestCodes(tours: TourSummary[], raw: string, limit = 6): TourSummary[] {
  const key = codeKey(raw);
  if (key.length < 2) return [];
  const common = (a: string) => {
    let i = 0;
    while (i < a.length && i < key.length && a[i] === key[i]) i++;
    return i;
  };
  const seen = new Set<string>();
  return tours
    .map((t) => ({ t, n: common(codeKey(t.code)) }))
    .filter(({ n }) => n >= Math.min(3, key.length))
    .sort((a, b) => b.n - a.n || a.t.code.localeCompare(b.t.code, "tr-TR"))
    .filter(({ t }) => (seen.has(t.code) ? false : (seen.add(t.code), true)))
    .slice(0, limit)
    .map(({ t }) => t);
}
